import { Language, Order, CartItem } from '../types';

// Format currency as Finnish standard: "14,50 €"
export function formatEuro(amount: number): string {
  const formatted = amount.toFixed(2).replace('.', ',');
  return `${formatted} €`;
}

// Calculate Finnish ALV (VAT) breakdowns
// Standard Finnish restaurant food ALV is 14%
// Standard Finnish beverage ALV is 25.5% (or 24%)
export function calculateVatBreakdown(items: { price: number; quantity: number; vat_rate: number }[]) {
  let subtotalNet = 0;
  let foodVat = 0;
  let beverageVat = 0;
  let grossTotal = 0;

  for (const item of items) {
    const itemTotal = item.price * item.quantity;
    grossTotal += itemTotal;

    // Gross price includes VAT: Net = Gross / (1 + vat/100)
    const rate = item.vat_rate || 14.0;
    const net = itemTotal / (1 + rate / 100);
    const vat = itemTotal - net;

    if (rate <= 15) {
      foodVat += vat;
    } else {
      beverageVat += vat;
    }
    subtotalNet += net;
  }

  return {
    grossTotal,
    subtotalNet,
    foodVat,
    beverageVat,
    totalVat: foodVat + beverageVat
  };
}

// Format Phone for WhatsApp URL (defaults to Finland +358 if 040... / 045... entered)
export function sanitizeWhatsAppPhone(phone: string): string {
  let cleaned = phone.replace(/[\s\-\(\)]/g, '');
  if (cleaned.startsWith('+')) {
    cleaned = cleaned.substring(1);
  } else if (cleaned.startsWith('0')) {
    cleaned = '358' + cleaned.substring(1);
  } else if (!cleaned.startsWith('358')) {
    cleaned = '358' + cleaned;
  }
  return cleaned;
}

// Pre-built WhatsApp Message generator (PRD requirement #8)
export function generateWhatsAppMessage(
  order: Order,
  type: 'confirmed' | 'preparing' | 'ready' | 'change_approved' | 'change_rejected',
  lang: Language = 'fi'
): string {
  const restaurant = 'The Oven Story';
  const table = order.table_number;
  const name = order.customer_name || 'Asiakas';

  if (lang === 'fi') {
    switch (type) {
      case 'confirmed':
        return `Hei ${name}! Tilauksesi ${order.order_number} pöytään ${table} on vahvistettu ravintolassa ${restaurant}. Valmistamme annokset pian uunissamme! 🔥`;
      case 'preparing':
        return `Hei ${name}! Tilauksesi ${order.order_number} (Pöytä ${table}) on nyt uunissa ja valmistumassa! 🍕`;
      case 'ready':
        return `Hei ${name}! Tilauksesi ${order.order_number} (Pöytä ${table}) on nyt valmis ja tarjoillaan hetken kuluttua pöytäänne. Hyvää ruokahalua! ✨`;
      case 'change_approved':
        return `Hei ${name}! Pyytämäsi muutos tilaukseen ${order.order_number} (Pöytä ${table}) on hyväksytty keittiössämme. 👍`;
      case 'change_rejected':
        return `Hei ${name}! Pahoittelemme, pyytämääsi muutosta tilaukseen ${order.order_number} ei voitu enää toteuttaa, koska annokset olivat jo uunissa.`;
    }
  } else if (lang === 'sv') {
    switch (type) {
      case 'confirmed':
        return `Hej ${name}! Din beställning ${order.order_number} för bord ${table} har bekräftats på ${restaurant}. Vi tillagar den strax! 🔥`;
      case 'preparing':
        return `Hej ${name}! Din beställning ${order.order_number} (Bord ${table}) är nu i ugnen! 🍕`;
      case 'ready':
        return `Hej ${name}! Din beställning ${order.order_number} (Bord ${table}) är nu klar och serveras strax. Smaklig måltid! ✨`;
      case 'change_approved':
        return `Hej ${name}! Din ändringsbegäran för beställning ${order.order_number} har godkänts. 👍`;
      case 'change_rejected':
        return `Hej ${name}! Vi ber om ursäkt, ändringen för ${order.order_number} kunde inte genomföras då tillagningen redan påbörjats.`;
    }
  } else {
    // English
    switch (type) {
      case 'confirmed':
        return `Hello ${name}, your order ${order.order_number} for Table ${table} has been confirmed at ${restaurant}. We are getting it ready for the wood-fired oven! 🔥`;
      case 'preparing':
        return `Hello ${name}, your order ${order.order_number} (Table ${table}) is now in the oven being prepared! 🍕`;
      case 'ready':
        return `Hello ${name}, your order ${order.order_number} (Table ${table}) is ready and will be served to your table shortly. Enjoy your meal! ✨`;
      case 'change_approved':
        return `Hello ${name}, your requested order change for ${order.order_number} has been approved by the kitchen. 👍`;
      case 'change_rejected':
        return `Hello ${name}, we apologize, your order change request for ${order.order_number} could not be accepted as the items are already in the oven.`;
    }
  }
}

// Generate direct WhatsApp link URL
export function createWhatsAppUrl(phone: string, text: string): string {
  const sanitized = sanitizeWhatsAppPhone(phone);
  const encoded = encodeURIComponent(text);
  return `https://wa.me/${sanitized}?text=${encoded}`;
}

// Format ISO date/time string to localized readable format
export function formatDateTime(isoString?: string): string {
  if (!isoString) return '';
  try {
    const d = new Date(isoString);
    return d.toLocaleString('fi-FI', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return isoString;
  }
}
